/**
 * Small, safe wrapper around localStorage.
 *
 * Everything is stored on this device only. Nothing is ever sent anywhere.
 * If localStorage is unavailable (private window, storage disabled) the app
 * still works — it just cannot remember progress between visits.
 */

const PREFIX = 'ohio-permit-arabic';

export const STORAGE_KEYS = {
  settings: `${PREFIX}:settings:v1`,
  progress: `${PREFIX}:progress:v1`,
  customQuestions: `${PREFIX}:custom-questions:v1`,
} as const;

let available: boolean | null = null;

export function storageAvailable(): boolean {
  if (available !== null) return available;
  try {
    const probe = `${PREFIX}:probe`;
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    available = true;
  } catch {
    available = false;
  }
  return available;
}

export function readJson<T>(key: string, fallback: T): T {
  if (!storageAvailable()) return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return fallback;
    const parsed = JSON.parse(raw) as T;
    return parsed ?? fallback;
  } catch (err) {
    console.warn(`[storage] Could not read "${key}":`, err);
    return fallback;
  }
}

export function writeJson(key: string, value: unknown): boolean {
  if (!storageAvailable()) return false;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (err) {
    console.warn(`[storage] Could not write "${key}":`, err);
    return false;
  }
}

export function removeKey(key: string): void {
  if (!storageAvailable()) return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** Triggers a download of `data` as a .json file. Used by Export Progress. */
export function downloadJsonFile(filename: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Give the browser a moment to start the download before releasing the URL.
  window.setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** Reads a user-picked file and parses it as JSON. */
export function readJsonFile(file: File): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('File could not be read.'));
    reader.onload = () => {
      try {
        resolve(JSON.parse(String(reader.result)));
      } catch (err) {
        reject(err instanceof Error ? err : new Error('Invalid JSON.'));
      }
    };
    reader.readAsText(file);
  });
}
